import React, { useEffect, useState } from "react";
import AttachmentTable from "~/Pages/Process/components/tabs/attachments/AttachmentTable";
import apiService from "~/services/service";
import { useParams } from "react-router-dom";
import { formatDate } from "~/utils/CSVFunctions";
import fileUploadLogo from "../../../assets/document.svg";
import { Card } from "primereact/card";
import API_ROUTES from "~/api-routes";
import DocumentConfirmationDialog from "~/components/confirmation-dialog/DocumentConfirmationDialog";
import TemporaryNotification from "~/components/toast-notification/AttachmentTemporaryNotification";
import Notification from "~/components/entities/notification/Notification";
import AttachmentSuccess from "~/components/toast-notification/AttachmentSuccess";
import Config from "~/config/env";
import { getPayslipBlobName } from "~/utils/fileUtils";
import { useDispatch, useSelector } from "react-redux";
import {
  handleDisableFilter,
  setBlobFilterStates,
  setRefetch,
} from "~/Pages/Process/actions/attachmentsSlice";

const SearchFIles = ({
  children,
  states,
  rows,
  first,
  onPageChange,
  isRootFolder = false,
}) => {
  const storageAccount = Config.BLOB_STORAGE_ACCOUNT;
  const [loading, setLoading] = useState(false);
  const [fileData, setFileData] = useState([]);
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(handleDisableFilter(isRootFolder ? false : !fileData?.length));
  }, [fileData]);

  const [attachmentConfirmModal, setAttachmentConfirmModal] = useState(false);
  const [temporaryMessage, setTemporaryMessage] = useState("");
  const [isAttachmentLoader, setIsAttachmentLoader] = useState(false);
  const [selectedFile, setSelectedFile] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [restoreMessage, setRestoreMessage] = useState("");
  const [relatedId, setRelatedId] = useState("");
  const { blobFilterStates } = useSelector((state) => state.attachments);

  // Helper function to filter files by entity_id
  const filterByEntityId = (files) => {
    if (!files?.length) return [];
    const entityId = states?.entity_detail?.id;
    return files.filter((file) => file.entity_id === entityId);
  };

  const getAttachments = async (id) => {
    setLoading(true);
    const type =
      states.selectedService &&
      states.selectedService.name == "Core Entity Information"
        ? states.selectedProcess
          ? states.selectedProcess.type
          : states.selectedType && states.selectedType.type
        : states.selectedService &&
            states.selectedService.name == "Ad Hoc Requests" &&
            states.selectedRequestType
          ? "AD_HOC"
          : "PROCESS";
    setRelatedId(id);

    // Don't make API call if type is null - it doesn't make sense without a document type
    if (!type) {
      console.warn("Document type is null, skipping API call");
      setLoading(false);
      setFileData([]);
      return;
    }

    let files;
    if (!states.isPayslipFolder) {
      const { data } = await apiService(API_ROUTES.FETCH_DOCUMENTS, "GET", {
        replace: { id, type },
      });
      // Filter files to only show those where entity_id matches the current entity's ID
      // AND have createdByData or name
      const entityId = states?.entity_detail?.id;
      files =
        data?.filter(
          (file) =>
            file.entity_id === entityId && (file.createdByData || file.name),
        ) || [];
    } else if (states.isPayslipFolder) {
      const { data } = await apiService(API_ROUTES.FETCH_DOCUMENTS, "GET", {
        replace: { id: id, type: "PAYSLIPS" },
      });
      files =
        data?.files
          ?.map((data) => ({
            name: getPayslipBlobName(data?.blobName) || data?.name,
            blobName: data?.blobName,
            createdAt: data?.createdAt,
            createdBy: data?.createdBy,
            file_type: data?.file_type,
            metadata: data?.metadata,
            original_file_name:
              data.source_filename || data?.original_file_name,
            createdByData: data?.createdByData,
            blob_url: `https://${storageAccount}.blob.core.windows.net/${"ess"}/${data?.blobName}`,
            size: data?.size,
            isPayslipData: true,
            category: "PAYSLIPS_AND_PAYROLL_REPORTS",
            subCategory: "PAYSLIPS",
          }))
          ?.filter((file) => file.createdByData || file.name) || [];
    }
    await setFileData(files);
    setLoading(false);
  };
  const deleteFile = (file) => {
    const filterData = blobFilterStates?.allData?.filter((data) => {
      return data?.blobName !== selectedFile?.blobName;
    });
    dispatch(setBlobFilterStates({ ...blobFilterStates, allData: filterData }));
  };
  const { service_id, process_id, request_id } = useParams();
  // GXM-1767: Determine the correct ID for fetching documents
  // For Core Entity Information subfolders (Officers, Shareholders, POA), use selectedProcess.id
  // For other cases, use process_id, request_id, or entity_id
  const getDocumentId = () => {
    if (process_id) return process_id;
    if (request_id) return request_id;
    // For Core Entity Information, selectedProcess contains the officer/shareholder data
    if (
      states.selectedService?.name === "Core Entity Information" &&
      states.selectedProcess?.id
    ) {
      return states.selectedProcess.id;
    }
    return states?.entity_detail?.id;
  };

  // GXM-1767: Check if we're in a Core Entity Information subfolder that requires selectedProcess
  const isCoreEntitySubfolder =
    states.selectedService?.name === "Core Entity Information" &&
    states.selectedType?.type &&
    ["OFFICER", "SHAREHOLDER", "POA", "DIRECTOR"].includes(
      states.selectedType.type,
    );
  useEffect(() => {
    if (states?.entity_detail?.name) {
      if (service_id) {
        // GXM-1767: For Core Entity subfolders, wait until selectedProcess is available
        // to avoid calling API with wrong entity ID
        if (isCoreEntitySubfolder && !states.selectedProcess?.id) {
          setLoading(true); // Show loading skeleton while waiting
          return;
        }
        getAttachments(getDocumentId());
      }
    }
  }, [
    states?.entity_detail,
    process_id,
    request_id,
    states.selectedProcess,
    states.selectedType,
  ]);

  const isCoreEntityOrRequestSelected =
    states.selectedService &&
    (states.selectedService.name === "Core Entity Information" ||
      states.selectedService.name === "Ad Hoc Requests");

  const handleDeleteDocument = (file) => {
    setAttachmentConfirmModal(true);
    setSelectedFile(file);
  };

  const relatedType =
    states.selectedService &&
    states.selectedService.name == "Core Entity Information"
      ? states.selectedProcess
        ? states.selectedProcess.type
        : states.selectedType && states.selectedType.type
      : states.selectedService &&
          states.selectedService.name == "Ad Hoc Requests" &&
          states.selectedRequestType
        ? "REQUEST"
        : "PROCESS";

  const handleConfirmDelete = async () => {
    setIsAttachmentLoader(true);
    setTemporaryMessage("");
    setRestoreMessage("");
    let isDeleted;
    if (!states?.isPayslipFolder) {
      const { status } = await apiService(
        API_ROUTES.DELETE_ATTACHMENT,
        "DELETE",
        {
          body: { blobUrl: selectedFile?.blob_url, name: selectedFile?.name },
          replace: {
            relatedId: process_id
              ? process_id
              : request_id
                ? request_id
                : states?.entity_detail?.id,
            relatedType:
              relatedType === "SHAREHOLDER" ? "SHAREHOLDERS" : relatedType,
          },
        },
      );
      isDeleted = status;
    } else if (states?.isPayslipFolder) {
      await apiService(API_ROUTES.DELETE_ATTACHMENT, "DELETE", {
        body: {
          blobUrl: selectedFile?.blob_url,
          name: selectedFile?.name,
          container: `ess`,
        },
        replace: {
          relatedId: process_id,
          relatedType,
        },
      });
      isDeleted = true;
    }

    if (isDeleted) {
      setTimeout(async () => {
        setLoading(true);
        if (blobFilterStates?.selectedItems?.length) {
          await dispatch(setRefetch(true));
        } else {
          await getAttachments(getDocumentId());
        }
        setAttachmentConfirmModal(false);
        setTemporaryMessage(
          `File ${states?.isPayslipFolder ? decodeURIComponent(selectedFile?.original_file_name) : selectedFile?.name} has been deleted.`,
        );
        setIsAttachmentLoader(false);
      }, 2000);
    }
  };

  const handleRestoreAttachment = async () => {
    setTemporaryMessage("");
    setRestoreMessage("");
    let isRestored;
    if (!states?.isPayslipFolder) {
      const { status } = await apiService(
        API_ROUTES.RESTORE_ATTACHMENT,
        "POST",
        {
          body: { blobUrl: selectedFile?.blob_url },
        },
      );
      isRestored = status;
    } else if (states?.isPayslipFolder) {
      const { status } = await apiService(
        API_ROUTES.RESTORE_ATTACHMENT,
        "POST",
        {
          body: { blobUrl: selectedFile?.blob_url, container: `ess` },
        },
      );
      isRestored = status;
    }
    if (isRestored) {
      setTimeout(async () => {
        if (blobFilterStates?.selectedItems?.length) {
          await dispatch(setRefetch(true));
        } else {
          await getAttachments(getDocumentId());
        }
        setRestoreMessage(
          `${states?.isPayslipFolder ? decodeURIComponent(selectedFile?.original_file_name) : selectedFile?.name} has been restored.`,
        );
      }, 2000);
    }
  };

  return (
    <div className="search__file--container">
      {/*<div className="autocomplete__container">
        <span className="pi pi-search search__icon"></span>
        <AutoComplete
          value={fileName}
          className="w-full"
          suggestions={selectedItem}
          completeMethod={(e) => searchItem(e)}
          onChange={(e) => setFileName(e.value)}
          onSelect={handleSelectDocument}
          field="label"
          placeholder="Search files by name or service line"
        />
      </div>
      <div className="flex flex-wrap gap-2 mt-3 mb-3">
        {["category", "services", "subcategory"].flatMap((type) =>
          selectedList[type].map((item, index) => {
            return (
              <Chip
                key={`${type}-${item.label}-${index}`}
                label={item.label}
                className="custom-chip"
                removable
                onRemove={() =>
                  setSelectedList((prev) => ({
                    ...prev,
                    [type]: prev[type].filter((_, i) => i !== index),
                  }))
                }
              />
            );
          }),
        )}
      </div>*/}

      {children}

      <Notification
        successMessage={successMessage}
        setSuccessMessage={setSuccessMessage}
      />
      <AttachmentSuccess
        successMsg={restoreMessage}
        setMessages={setRestoreMessage}
      />
      <TemporaryNotification
        onUndo={handleRestoreAttachment}
        successMsg={temporaryMessage}
        setMessages={setTemporaryMessage}
      />
      {blobFilterStates?.selectedItems?.length ? (
        // Filters are applied
        blobFilterStates?.filterLoading ||
        filterByEntityId(blobFilterStates?.allData)?.length ? (
          <AttachmentTable
            formatDate={formatDate}
            processAttachments={filterByEntityId(blobFilterStates?.allData)}
            loadingAttachments={blobFilterStates?.filterLoading}
            first={first}
            rows={rows}
            onPageChange={onPageChange}
            showTypeColumn={!isCoreEntityOrRequestSelected}
            handleAttachmentDelete={handleDeleteDocument}
            attachmentConfirmModal={attachmentConfirmModal}
            setAttachmentConfirmModal={setAttachmentConfirmModal}
            handleConfirmDelete={handleConfirmDelete}
            isPayslipFolder={states?.isPayslipFolder}
            relatedType={relatedType}
            relatedId={relatedId}
            totalRecords={filterByEntityId(blobFilterStates?.allData)?.length}
            isFilterApplied={blobFilterStates?.selectedItems?.length}
            onRefetch={() => getAttachments(getDocumentId())}
            nameSubtext={(row) =>
              row?.breadcrumb ? (
                <span
                  className="text-color-secondary"
                  style={{ fontSize: "0.8rem", color: "#6c757d" }}
                >
                  {row.breadcrumb}
                </span>
              ) : null
            }
          />
        ) : (
          // Filter applied, not loading, no data - show empty state
          <>
            <Card>
              <div className="flex flex-column justify-content-center align-items-center p-5">
                <div className="no-documents-uploaded-section w-full gap-4">
                  <img
                    src={fileUploadLogo}
                    alt="file-upload"
                    className="file-upload-logo"
                  />
                  <p className="add-new-entity-heading mb-0">
                    No documents have been added yet
                  </p>
                </div>
              </div>
            </Card>
          </>
        )
      ) : fileData?.length || loading ? (
        // No filters applied - show regular data
        <AttachmentTable
          formatDate={formatDate}
          processAttachments={fileData}
          loadingAttachments={loading}
          first={first}
          rows={rows}
          onPageChange={onPageChange}
          showTypeColumn={!isCoreEntityOrRequestSelected}
          handleAttachmentDelete={handleDeleteDocument}
          attachmentConfirmModal={attachmentConfirmModal}
          setAttachmentConfirmModal={setAttachmentConfirmModal}
          handleConfirmDelete={handleConfirmDelete}
          isPayslipFolder={states?.isPayslipFolder}
          relatedType={relatedType}
          relatedId={relatedId}
          totalRecords={fileData?.length}
          onRefetch={() => getAttachments(getDocumentId())}
        />
      ) : (
        <>
          {/*Object.values(selectedList).flat().length ? (
          <div className="empty__data__view--container">
            <img src={searchIcon} />
            <p>Sorry, we couldn’t find what you’re looking for.</p>
            <p>Try searching for something else or use a different tag.</p>
            <div className="suggested__tags--container">
              <p>Suggested Tags</p>
              <div>
                <Chip label="Passport" />
                <Chip label="Contract" />
                <Chip label="Invoice" />
                <Chip label="Other" />
              </div>
            </div>
          </div>*/}
          <Card>
            <div className="flex flex-column justify-content-center align-items-center p-5">
              <div className="no-documents-uploaded-section w-full gap-4">
                <img
                  src={fileUploadLogo}
                  alt="file-upload"
                  className="file-upload-logo"
                />
                <p className="add-new-entity-heading mb-0">
                  No documents have been added yet
                </p>
              </div>
            </div>
          </Card>
        </>
      )}
      {attachmentConfirmModal && (
        <DocumentConfirmationDialog
          visible={attachmentConfirmModal}
          closeModal={() => setAttachmentConfirmModal(false)}
          handleSave={handleConfirmDelete}
          isLoader={isAttachmentLoader}
        />
      )}
    </div>
  );
};

export default SearchFIles;
